import { useEffect, useRef } from 'react'
import { usePostHog } from 'posthog-js/react'

// One vocabulary for the onboarding funnel. Every step emits
// `onboarding_step_viewed` on entry and `onboarding_step_completed` (with the
// time spent) on exit, so a single PostHog funnel over `step` shows where people
// leave and how long each screen holds them. Step-specific events (website
// skipped, enrichment finished, ...) carry the same `step` property.
//
// The older one-off events (onboarding_started, onboarding_language_set,
// onboarding_moodboard_created, onboarding_completed) are kept as they were so
// existing insights keep working.

export type OnboardingStep = 'appsumo' | 'language' | 'website' | 'canvas' | 'finish'

const STEP_INDEX: Record<OnboardingStep, number> = {
  appsumo: 0,
  language: 1,
  website: 2,
  canvas: 3,
  finish: 4,
}

/** Emits `onboarding_step_viewed` once when `active` first turns true, and
 *  returns `complete()`, which emits `onboarding_step_completed` with
 *  `duration_ms` since the view. Also returns `track()` for step-scoped events. */
export function useOnboardingStep(step: OnboardingStep, active = true) {
  const posthog = usePostHog()
  const viewedAtRef = useRef<number | null>(null)

  useEffect(() => {
    if (!active || viewedAtRef.current !== null) return
    viewedAtRef.current = Date.now()
    posthog.capture('onboarding_step_viewed', { step, step_index: STEP_INDEX[step] })
  }, [active, step, posthog])

  const sinceView = () => (viewedAtRef.current === null ? null : Date.now() - viewedAtRef.current)

  const track = (event: string, properties: Record<string, unknown> = {}) => {
    posthog.capture(event, {
      step,
      step_index: STEP_INDEX[step],
      ms_since_step_view: sinceView(),
      ...properties,
    })
  }

  const complete = (properties: Record<string, unknown> = {}) => {
    posthog.capture('onboarding_step_completed', {
      step,
      step_index: STEP_INDEX[step],
      duration_ms: sinceView(),
      ...properties,
    })
  }

  return { track, complete }
}
